import { QueryClient, UseMutationOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { produce } from 'immer';

import { AwardType, Bonus, CachedBonuses } from '../../../../../types/bonus';
import { updateCachedBonus } from '../../cache/mutators/bonus';
import { QueryKey } from '../../hooks/types';
import { RecognizeResponse } from '../../types/api';
import { UserProfile } from '../../types/user';

export const addUserReaction = (bonus: Bonus, user: UserProfile): Bonus =>
	produce(bonus, (draft) => {
		draft.reactions.push({
			_id: Math.random().toString(),
			emoji: ':heart:',
			user_id: user.id,
			user_name: user.display_name,
		});
	});

export const removeUserReaction = (bonus: Bonus, user: UserProfile): Bonus =>
	produce(bonus, (draft) => {
		draft.reactions = draft.reactions.filter((reaction) => reaction.user_id !== user.id);
	});

export const getRecognitionType = (bonus: Bonus) => {
	return bonus.award_type === null
		? 'peer_to_peer_recognition'
		: bonus.award_type === AwardType.Claimable
			? 'claimable_award'
			: bonus.award_type === AwardType.Award
				? 'manual_award'
				: 'automated_award';
};

interface OptimisticConfig {
	bonus: Bonus;
	client: QueryClient;
	modifier: (bonus: Bonus, user: UserProfile) => Bonus;
	user: UserProfile;
}

type ReactionMutationConfig<T = unknown> = Pick<
	UseMutationOptions<AxiosResponse<RecognizeResponse<Bonus>>, unknown, T, { shouldReload: boolean }>,
	'onMutate' | 'onSettled'
>;

export const reactionMutationConfig = <T = unknown>({ modifier, client, user, bonus }: OptimisticConfig): ReactionMutationConfig<T> => ({
	onMutate: () => {
		const shouldReload = !!client.isFetching([QueryKey.BonusesInfinite]);

		if (shouldReload) {
			client.cancelQueries([QueryKey.BonusesInfinite]);
		}

		client.setQueriesData<CachedBonuses>([QueryKey.BonusesInfinite], (cached) => updateCachedBonus(modifier(bonus, user), cached));

		return { shouldReload };
	},
	onSettled: (_data, _err, _vars, context) => {
		if (context?.shouldReload) {
			client.invalidateQueries([QueryKey.BonusesInfinite]);
		}
	},
});
